import { FeatureFlag } from 'featureFlags';
import { getAppServices } from 'providers/app-services';
import {
  License,
  licensePackageFromString,
  LicensePackageOptions,
} from 'records/License';
import { DateTime } from 'luxon';
import GQLRequest from '../utils/GQLRequest';

export const getLicense = async (): Promise<License> => {
  const { authService, featureFlagService } = getAppServices();
  const licenseStub = featureFlagService.getFeature<
    | LicensePackageOptions
    | 'N/A'
    | 'Trial'
    | 'ExpiredTrial'
    | 'ExpiredSubscription'
    | 'Core'
    | 'Basics'
    | 'Complete'
  >(FeatureFlag.LicenseStub);

  if (licenseStub === 'Trial') {
    const trialDurationInDays = 14;
    const trialExpirationDate = DateTime.utc().plus({
      days: trialDurationInDays,
    });
    const trialDaysRemaining = Math.max(
      Math.floor(trialExpirationDate.diffNow('days').days),
      0
    );

    return new License({
      package: licensePackageFromString('Complete'),
      isTrial: true,
      trialDaysRemaining,
      subscriptionExpiration: trialExpirationDate.toString(),
    });
  }

  if (licenseStub === 'ExpiredTrial') {
    const trialExpirationDate = DateTime.now().minus({ days: 1 }).toUTC();
    const trialDaysRemaining = 0;

    return new License({
      package: licensePackageFromString('Unpaid'),
      isTrial: true,
      trialDaysRemaining,
      subscriptionExpiration: trialExpirationDate.toString(),
    });
  }

  if (licenseStub === 'ExpiredSubscription') {
    const subscriptionExpiration = DateTime.now().minus({ days: 1 }).toUTC();

    return new License({
      package: licensePackageFromString('Unpaid'),
      isTrial: false,
      trialDaysRemaining: 0,
      subscriptionExpiration: subscriptionExpiration.toString(),
    });
  }

  // for testing purposes on DEV as default rule for feature flag is 'Complete'
  if (
    licenseStub === 'Complete' ||
    licenseStub === 'Basics' ||
    licenseStub === 'Core'
  ) {
    return new License({
      package: licensePackageFromString(
        licenseStub === 'Complete'
          ? 'Complete'
          : licenseStub === 'Basics'
          ? 'Basics'
          : 'Core'
      ),
      isTrial: false,
      trialDaysRemaining: 0,
      subscriptionExpiration: '9999-12-31T00:00:00.00+00:00', // signifies an "infinity" or "no expiration" date
    });
  }

  const { idToken } = authService.getCredentials();
  const res = await new GQLRequest()
    .query(
      `
        query GetLicense($idToken: String!) {
          GetLicense(idToken: $idToken) {
            package,
            isTrial,
            trialDaysRemaining
            subscriptionExpiration,
            accountId,
            salesforceId
          }
        }
      `
    )
    .variables({ idToken })
    .json<{
      GetLicense: {
        package: string;
        isTrial: boolean;
        trialDaysRemaining: number;
        subscriptionExpiration: string;
        accountId?: string;
        salesforceId?: string;
      };
    }>();
  const {
    package: licensePackage,
    isTrial,
    trialDaysRemaining,
    subscriptionExpiration,
    accountId,
    salesforceId,
  } = res.GetLicense;

  const isSubscriptionExpired =
    DateTime.fromISO(subscriptionExpiration) < DateTime.now();
  const isTrialExpired = isTrial && trialDaysRemaining <= 0;
  // When licenseStub is N/A return license constructed from the query response
  return new License({
    package: licensePackageFromString(
      isTrialExpired || isSubscriptionExpired ? 'Unpaid' : licensePackage
    ),
    isTrial,
    trialDaysRemaining,
    subscriptionExpiration,
    accountId,
    salesforceId,
  });
};
